// TEMP CORE-9199 remove after Oct 30
import { useEffect, useState, type KeyboardEvent } from 'react'
import { DateTime } from 'luxon'
import { Button } from '@/ui'
import {
  ART_CAUSES,
  CHECKBOX_FIELDS,
  HANDOFF_REASONS,
  NOTE_TYPES,
  OUTCOMES,
  SELF_SERVE_CLOSE,
  emptyState,
  loadState,
  saveState,
  toMarkdown,
  type CheckboxField,
  type ShadowState,
} from './shadowNotes'

const elapsed = (startedAt: string | null): string =>
  startedAt
    ? DateTime.now().diff(DateTime.fromISO(startedAt)).toFormat('mm:ss')
    : '--:--'

const useClock = (): void => {
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((tick) => tick + 1), 1000)
    return () => clearInterval(id)
  }, [])
}

export const ShadowNotesPanel = ({
  userId,
  currentStep,
}: {
  userId: number
  currentStep: string | null
}) => {
  const [state, setState] = useState<ShadowState>(() => loadState(userId))
  const [noteType, setNoteType] = useState<string>(NOTE_TYPES[0])
  const [text, setText] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  useClock()

  useEffect(() => saveState(userId, state), [userId, state])

  const update = (patch: Partial<ShadowState>) =>
    setState((prev) => ({ ...prev, ...patch }))

  const addNote = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || !text.trim()) return
    event.preventDefault()
    const line = `${elapsed(state.startedAt)} · ${currentStep ?? 'unknown step'} · ${noteType}: ${text.trim()}`
    update({ notes: [...state.notes, line] })
    setText('')
  }

  const toggleCheck = (field: CheckboxField) => {
    const checks = { ...state.checks, [field]: !state.checks[field] }
    const clearsSelfServe =
      checks['Admin action taken'] && state.outcome === SELF_SERVE_CLOSE
    update({ checks, outcome: clearsSelfServe ? '' : state.outcome })
  }

  const toggleReason = (reason: string) =>
    update({
      handoffReasons: state.handoffReasons.includes(reason)
        ? state.handoffReasons.filter((r) => r !== reason)
        : [...state.handoffReasons, reason],
    })

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(toMarkdown(state))
      setCopied('Copied')
    } catch {
      setCopied('Copy failed: select the preview below and copy by hand')
    }
  }

  const reset = () => {
    if (window.confirm('Clear all notes and scoring for this user?'))
      setState(emptyState())
  }

  return (
    <div className="sticky top-4 space-y-4 rounded border p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Shadow notes</h3>
        <span className="font-mono">{elapsed(state.startedAt)}</span>
      </div>
      <Button
        variant="secondary"
        onClick={() => update({ startedAt: DateTime.now().toISO() })}
      >
        {state.startedAt ? 'Restart call clock' : 'Start call'}
      </Button>

      <div className="space-y-2">
        <select
          className="w-full rounded border p-1"
          value={noteType}
          onChange={(e) => setNoteType(e.target.value)}
        >
          {NOTE_TYPES.map((type) => (
            <option key={type}>{type}</option>
          ))}
        </select>
        <textarea
          className="h-20 w-full rounded border p-2"
          placeholder="Type a note, Enter to save (Shift+Enter for a new line)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={addNote}
        />
        <ul className="max-h-60 overflow-auto text-sm">
          {state.notes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-2">
        <h4 className="font-semibold">Scoring</h4>
        {CHECKBOX_FIELDS.map((field) => (
          <label key={field} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={state.checks[field]}
              onChange={() => toggleCheck(field)}
            />
            {field}
          </label>
        ))}
        <label className="block">
          Outcome
          <select
            className="w-full rounded border p-1"
            value={state.outcome}
            onChange={(e) => update({ outcome: e.target.value })}
          >
            <option value="">(not set)</option>
            {OUTCOMES.map((outcome) => (
              <option
                key={outcome}
                disabled={
                  outcome === SELF_SERVE_CLOSE &&
                  state.checks['Admin action taken']
                }
              >
                {outcome}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>Handoff reason</legend>
          {HANDOFF_REASONS.map((reason) => (
            <label
              key={reason}
              className="mr-3 inline-flex items-center gap-1 text-sm"
            >
              <input
                type="checkbox"
                checked={state.handoffReasons.includes(reason)}
                onChange={() => toggleReason(reason)}
              />
              {reason}
            </label>
          ))}
        </fieldset>
        <label className="block">
          Art cause
          <select
            className="w-full rounded border p-1"
            value={state.artCause}
            onChange={(e) => update({ artCause: e.target.value })}
          >
            <option value="">(not set)</option>
            {ART_CAUSES.map((cause) => (
              <option key={cause}>{cause}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex gap-2">
        <Button onClick={copy}>Copy as Markdown</Button>
        <Button variant="ghost" onClick={reset}>
          Clear
        </Button>
      </div>
      {copied && <p className="text-sm">{copied}</p>}
      <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-neutral-100 p-2 text-xs">
        {toMarkdown(state)}
      </pre>
    </div>
  )
}
